import React from "react";
import LinkHierarchyHelper from "../../link/LinkHierarchyHelper";
import HelperFunctions from "../../common/HelperFunctions";

/**
 * Truncates the endpoint name checking if it follows the following pattern:
 * <site-code>-<rest-of-device-name>_<interface-name> -> <site-code>...<interface-name>
 * If it is not possible to truncate, display the entire name.
 */
export const truncateEndpointName = (endpointName) => {
    const lastUnderscoreIdx = endpointName.lastIndexOf("_");

    const firstDelimiterIdx = endpointName.search(/[-_ ]/);

    if (lastUnderscoreIdx === -1 || firstDelimiterIdx === -1) {
        return endpointName;
    }
    return `${endpointName.substring(0, firstDelimiterIdx)}...${endpointName.substring(lastUnderscoreIdx + 1)}`;
};

/**
 * How a link ID should be rendered.
 * NOTE: it is assumed that this component is ONLY USED ON TRAILBLAZER page,
 * where the window href would end in `/linkservice/trailblazerVisualization`
 */
export const renderLinkIdAsLink = (instanceId) => {
    const uuid = HelperFunctions.getUUID(instanceId);
    if (!uuid) {
        return <div>{ instanceId }</div>;
    }

    const truncatedUuid = `${uuid.substring(0, 4)}...${uuid.substring(uuid.length - 4, uuid.length)}`;
    const linkSearchPath =
        `${window.location.origin}/linkservice/linkDetails/`;

    return (
        <a href={linkSearchPath + uuid}>
            {truncatedUuid}
        </a>
    );
};

/**
 * Sets all falsy values to "N/A" except 0.
 */
export const displayFalsyValue = (value) => {
    if (value === 0 || value === false) {
        return value;
    }
    return value || "N/A";
};

/**
 * The metadata renderer is a mapping which field on the metadata for the Trailblazer API response
 * need to be rendered in a unique way (aside from text).
 */
const METADATA_RENDERERS = {
    // link IDs should be rendered as truncated links with href leading to details page
    linkId: value => renderLinkIdAsLink(value),
    // type IDs should be rendered as the readable link type
    typeId: value => LinkHierarchyHelper.getReadableLinkTypeFromTypeIdAndStage(value),
    // position should be rendered without being falsy for zero
    position: value => displayFalsyValue(value)
};

/**
 * Component that renders any metadata value with appropriate formatting.
 */
export const MetadataRenderer = ({ fieldName, value }) => {
    // if we have a special renderer for this field and the value exists
    if (METADATA_RENDERERS[fieldName]) {
        return METADATA_RENDERERS[fieldName](value);
    }

    return value || "-";
};

/**
 * Sorts the list of keys (strings) using the given config.
 * The config should either be an object with the following interface:
 * { priority: number } or an array of keys.
 */
export const sortMetadataKeys = (metadataKeys, config) => {
    if (!config) {
        return metadataKeys.sort((keyA, keyB) => keyA.localeCompare(keyB));
    }

    if (Array.isArray(config)) {
        // Array-based ordering
        return metadataKeys.sort((keyA, keyB) => {
            const indexA = config.indexOf(keyA);
            const indexB = config.indexOf(keyB);

            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }

            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            return keyA.localeCompare(keyB);
        });
    }
    // Object-based priority ordering
    return metadataKeys.sort((keyA, keyB) => {
        const priorityA = config[keyA]?.priority || 999;
        const priorityB = config[keyB]?.priority || 999;

        if (priorityA === priorityB) {
            return keyA.localeCompare(keyB);
        }

        return priorityA - priorityB;
    });
};

/**
 * Given a metadata object, returns an ordered list of key-value pairs.
 */
export const sortMetadataEntries = (metadata, config) => {
    const sortedMetadataKeys = sortMetadataKeys(Object.keys(metadata), config);
    const sortedMetadata = [];
    sortedMetadataKeys.forEach((key) => {
        sortedMetadata.push([key, metadata[key]]);
    });
    return sortedMetadata;
};
